import { useState } from 'react';
import {
    Upload,
    MessageSquare,
    Users,
    Sparkles,
    ChevronRight,
    Calendar,
    FileText,
    Play,
    Brain,
    Target,
    UserPlus,
    ArrowRight,
    Clock
} from 'lucide-react';
const Main = () => {
    const [stage, setStage] = useState('input');
    const [processingStatus, setProcessingStatus] = useState<any>(null);

    // Mock AI processing states
    const [aiAnalysis, setAiAnalysis] = useState<any>(null);
    const mockMeetingIntel = {
        summary: "Client expressed strong interest in global IP protection strategy. Key concerns around AI patent protection and trademark portfolio management. Budget confirmed at £200k+. Timeline: Q2 implementation.",
        keyPoints: [
            "AI/ML patent portfolio expansion needed",
            "Multi-territory trademark strategy",
            "Concerned about competitive landscape",
            "Previous experience with delayed filings"
        ],
        sentiment: "Very Positive",
        nextSteps: [
            "Technical review meeting",
            "Portfolio analysis",
            "Budget proposal"
        ],
        riskFactors: [
            "Timeline sensitivity",
            "Technical complexity"
        ]
    };

    // Mock AI recommendations
    const aiRecommendations: any = {
        playbook: {
            name: "Enterprise AI Protection Strategy",
            confidence: 92,
            timeline: "3-4 months",
            expectedValue: "£250,000",
            approach: [
                "Technical Deep Dive",
                "Portfolio Strategy Session",
                "Commercial Proposal"
            ]
        },
        sequence: {
            type: "High-Touch Technical",
            steps: [
                {
                    stage: "Technical Assessment",
                    timeline: "Week 1",
                    owner: "Technical Lead"
                },
                {
                    stage: "Strategy Development",
                    timeline: "Week 2-3",
                    owner: "IP Strategist"
                },
                {
                    stage: "Commercial Discussion",
                    timeline: "Week 4",
                    owner: "Commercial Lead"
                }
            ]
        },
        teamMatch: {
            primary: {
                name: "Sarah Chen",
                role: "Technical Lead",
                matchScore: 95,
                expertise: ["AI/ML", "Patent Strategy"]
            },
            support: [
                {
                    name: "James Wilson",
                    role: "IP Strategist",
                    matchScore: 88,
                    expertise: ["Portfolio Management"]
                },
                {
                    name: "Emma Roberts",
                    role: "Commercial Lead",
                    matchScore: 92,
                    expertise: ["Enterprise Sales"]
                }
            ]
        }
    };

    const processLead = () => {
        setProcessingStatus('analyzing');
        // Simulate AI processing
        setTimeout(() => {
            setAiAnalysis(aiRecommendations);
            setProcessingStatus('complete');
        }, 2000);
    };
    return (
        <div className="space-y-6">
            {/* Lead Input Form */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                       <span className='font-bold'>Lead Details</span>
                    </div>
                    <div>
                        Enter lead information or upload meeting notes
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium">Company Name</label>
                                <input type="text" placeholder="Enter company name" className="w-full border-4 border-[#033d44] p-2.5 text-black bg-[#fbfbfb] rounded-2xl mb-3 focus:outline-none"></input>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Industry</label>
                                <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                                    <option value="">Select Industry</option>
                                    <option value="tech">Technology</option>
                                    <option value="biotech">Biotech</option>
                                    <option value="fintech">Fintech</option>
                                </select>
                            </div>
                        </div>

                        {/* Meeting Notes */}
                        <div>
                            <label className="text-sm font-medium">Meeting Notes or Requirements</label>
                            <textarea className="focus:outline-none shadow-xl mb-3 border-4 border-[#033d44] resize-none block p-2.5 w-full text-black bg-[#fbfbfb] rounded-2xl border focus:ring-mainBlue focus:border-mainBlue h-32"  placeholder="Enter meeting notes, requirements, or key points..."></textarea>
                        </div>

                        {/* Upload Options */}
                        <div className="flex items-center gap-4">
                            <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                <Upload className="w-4 h-4" />
                                Upload Meeting Recording
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                <FileText className="w-4 h-4" />
                                Upload Documents
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Analysis */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5" />
                       <span className='font-bold'>AI Meeting Intelligence</span>
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {/* Summary */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <h3 className="font-medium mb-2">Meeting Summary</h3>
                            <p className="text-sm text-gray-600">{mockMeetingIntel.summary}</p>
                        </div>

                        {/* Key Points */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <h3 className="font-medium mb-2">Key Points</h3>
                                <div className="space-y-2">
                                    {mockMeetingIntel.keyPoints.map((point, index) => (
                                        <div key={index} className="flex items-center gap-2 text-sm">
                                            <ChevronRight className="w-4 h-4" />
                                            {point}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="font-medium mb-2">Risk Factors</h3>
                                <div className="space-y-2">
                                    {mockMeetingIntel.riskFactors.map((risk, index) => (
                                        <div key={index} className="flex items-center gap-2 text-sm">
                                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                            {risk}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg justify-content-center" onClick={processLead}>
                            <Sparkles className="w-4 h-4" />
                            Generate Recommendations
                        </button>
                    </div>
                </div>
            </div>

            {/* AI Recommendations */}
            {aiAnalysis && (
                <>
                    {/* Playbook Recommendation */}
                    <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                        <div>
                            <div className="flex items-center gap-2">
                                <Play className="w-5 h-5" />
                               <span className='font-bold'>Recommended Playbook</span>
                            </div>
                        </div>
                        <div>
                            <div className="space-y-4 mt-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-medium">{aiAnalysis.playbook.name}</h3>
                                        <p className="text-sm text-gray-500">
                                            Timeline: {aiAnalysis.playbook.timeline} •
                                            Value: {aiAnalysis.playbook.expectedValue}
                                        </p>
                                    </div>
                                    <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                        {aiAnalysis.playbook.confidence}% Match
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Approach</h4>
                                        <div className="space-y-2">
                                            {aiAnalysis.playbook.approach.map((step: any, index: any) => (
                                                <div key={index} className="flex items-center gap-2 text-sm">
                                                    <ArrowRight className="w-4 h-4" />
                                                    {step}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Sequence</h4>
                                        <div className="space-y-2">
                                            {aiAnalysis.sequence.steps.map((step: any, index: any) => (
                                                <div key={index} className="text-sm">
                                                    <div className="font-medium">{step.stage}</div>
                                                    <div className="text-gray-500">{step.timeline} • {step.owner}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Team Recommendations */}
                    <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                        <div>
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                <span className='font-bold'>Recommended Team</span>
                            </div>
                        </div>
                        <div>
                            <div className="space-y-4 mt-2">
                                {/* Primary Lead */}
                                <div className="p-4 border rounded-lg">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h3 className="font-medium">{aiAnalysis.teamMatch.primary.name}</h3>
                                            <p className="text-sm text-gray-500">{aiAnalysis.teamMatch.primary.role}</p>
                                        </div>
                                        <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                            {aiAnalysis.teamMatch.primary.matchScore}% Match
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {aiAnalysis.teamMatch.primary.expertise.map((exp: any, index: any) => (
                                            <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800' key={index}>{exp}</div>
                                        ))}
                                    </div>
                                </div>

                                {/* Support Team */}
                                <div className="grid grid-cols-2 gap-4">
                                    {aiAnalysis.teamMatch.support.map((member: any, index: any) => (
                                        <div key={index} className="p-4 border rounded-lg">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h3 className="font-medium">{member.name}</h3>
                                                    <p className="text-sm text-gray-500">{member.role}</p>
                                                </div>
                                                <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                                    {member.matchScore}% Match
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {member.expertise.map((exp: any, idx: any) => (
                                                    <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800' key={idx}>{exp}</div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-end gap-2">
                                    <button className='flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green'>Modify Team</button>
                                    <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                        <Calendar className="w-4 h-4" />
                                        Schedule Kickoff
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
export default Main